import React from 'react';
import { FormattedMessage } from 'react-intl';
import type { RolesApi } from '@env0/role-service/api';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Item, Panel, PanelSection } from 'components/layout/navbar/navbar-panel.utils';
import { applyRailGroups, type RailGroup } from 'components/layout/navbar/navbar-rail-groups';
import { typeStyle } from 'constants/typography';

export interface VerticalTabItem {
  key: string;
  icon: React.ComponentType<any>;
  labelId: string;
  component: React.ComponentType<any>;
  showInMobile?: boolean;
  requiredPermission?: RolesApi.RBACPermission | RolesApi.RBACPermission[];
  isHiddenFeature?: boolean;
}

interface VerticalTabsMenuProps {
  items: VerticalTabItem[];
  activeKey?: string;
  onTabChange: (key: string) => void;
  componentProps?: any;
  dataE2e?: string;
  groups?: RailGroup[];
}

const Content = styled.div`
  flex: 1;
  min-width: 0;
  padding: var(--padding-xxl) var(--padding-lg);
  overflow-y: scroll;
  box-sizing: border-box;
`;

const MenuItem = styled(Item)`
  /* Default text and icon color */
  color: var(--text-body);

  /* Override Item's nested SVG color/stroke to follow current text color */
  svg {
    color: var(--icon-secondary);
  }

  /* Colours only. The active row's geometry comes from the shared Item, which Figma specifies the same
     way on either surface; declaring a marker here too painted a second one over it. Item styles the
     dark rail, so its colours are overridden here on emotion's composition order. */
  ${({ active, noHover }) => css`
    ${
      active &&
      css`
        background-color: var(--surface-secondary-background);
        color: var(--text-heading);
        svg {
          color: var(--icon-primary-standalone);
        }
      `
    }

    ${
      !noHover &&
      css`
        /* Menu Item / Secondary gives Hover and Active the same fill, which is this token. */
        &:hover {
          background-color: var(--surface-secondary-background);
        }
      `
    }
  `}
`;

const VerticalTabsMenu: React.FC<VerticalTabsMenuProps> = ({
  items,
  activeKey,
  onTabChange,
  componentProps,
  dataE2e = 'tabs-settings',
  groups
}) => {
  const activeItem = items.find(item => item.key === activeKey);
  const ActiveComponent = activeItem?.component;
  const entries = groups ? applyRailGroups(items, groups) : items;

  return (
    <Container data-e2e={dataE2e}>
      <Sidebar>
        <Panel>
          <PanelSection>
            {entries.map(entry => {
              if ('isSectionTitle' in entry) {
                return (
                  <SectionLabelContainer key={entry.key} data-e2e={entry.key}>
                    <SectionLabel>{(entry as { title?: React.ReactNode }).title}</SectionLabel>
                  </SectionLabelContainer>
                );
              }

              const Icon = entry.icon;
              const isActive = entry.key === activeKey;

              return (
                <MenuItem
                  key={entry.key}
                  data-e2e={`menu-${entry.key}`}
                  onClick={() => onTabChange(entry.key)}
                  active={isActive}>
                  <Icon size={16} />
                  <span>
                    <FormattedMessage id={entry.labelId} />
                  </span>
                </MenuItem>
              );
            })}
          </PanelSection>
        </Panel>
      </Sidebar>

      <Content>{ActiveComponent && componentProps && <ActiveComponent {...componentProps} />}</Content>
    </Container>
  );
};

/**
 * Figma nests each group in its own column at 16/8/4; this nav renders one flat list, so the label
 * carries the difference on top of PanelSection's 4px gap.
 */
const SectionLabelContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 0 var(--padding-md);
  margin-top: var(--padding-md);
  margin-bottom: var(--spacing-xxxs);
`;

const SectionLabel = styled.span`
  ${typeStyle.labelMd}
  color: var(--text-secondary);
`;

const Container = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const Sidebar = styled.div`
  width: 150px;
  height: 100%;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  background-color: var(--surface-main-page);
  box-sizing: border-box;
  border-right: 1px solid var(--border-primary);
  padding: var(--padding-lg) 0;
`;

export default VerticalTabsMenu;
